// ExamHistory.js
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./ExamHistory.css";

// Import Ant Design icons
import {
  ArrowLeftOutlined,
  WarningOutlined,
  HistoryOutlined
} from '@ant-design/icons';

// Import components
import HistoryItem from "../../Components/HistoryItem/HistoryItem";
import axiosInstance from "../../Config/axiosConfig";

const ExamHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState('');
  console.log('response user id', userId)
  const [stats, setStats] = useState({ 
    completed: 0, 
    averageScore: 0,
    totalExams: 0 
  });
  
  // Get data passed from parent component if available
  const passedData = location.state?.assessmentData;

  // Fetch assessment results from API
  const fetchAssessmentResults = async () => {
  try {
    setError(null);
    setLoading(true);
    
    // Get required data from localStorage
    const accessToken = localStorage.getItem("accessToken");
    const storedDeviceId = localStorage.getItem('deviceId');
    const storedUserIp = localStorage.getItem('userIp');

    // Get user ID from localStorage - directly use the variable, don't rely on state
    let userIdFromStorage = '';
    const userInfoString = localStorage.getItem("userInfo");
    
    if (userInfoString) {
      try {
        const userInfo = JSON.parse(userInfoString);
        userIdFromStorage = userInfo.userId; // Store in local variable
        setUserId(userIdFromStorage); // Still update state for other uses
        console.log("User ID from storage:", userIdFromStorage);
      } catch (parseError) {
        console.error("Error parsing userInfo:", parseError);
        setError("Invalid user information");
        setLoading(false);
        return;
      }
    } else {
      setError("Please login to view assessment history");
      setLoading(false);
      return;
    }

    if (!accessToken) {
      setError("Authentication required. Please login again.");
      setLoading(false);
      return;
    }

    // Make API request using the LOCAL variable, not the state
    const response = await axiosInstance.get(
      `Assessment/get-user-assessment-results/${userIdFromStorage}`, // Use the local variable here
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          UserDevice: storedDeviceId,
          UserIPAddress: storedUserIp,
        },
      }
    );

    console.log('API Response:', JSON.stringify(response.data, null, 2));

    if (response.data && response.data.code === 200) {
      // Transform API data to match our component structure
      const transformedData = transformApiData(response.data.data);
      setData(transformedData);
      
      // Calculate stats
      calculateStats(transformedData);
    } else {
      setError(response.data?.message || 'Failed to fetch assessment results');
    }

  } catch (error) {
    console.error('Error fetching assessment results:', error);
    setError(error.message || 'An error occurred while fetching data');
    setData([]); // Clear any previous data on error
  } finally {
    setLoading(false);
    setRefreshing(false);
  }
};

  // Transform API response to match our component data structure
  const transformApiData = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) return [];

    return apiData.map((item, index) => ({
      id: item.assessmentId?.toString() || index.toString(),
      title: item.name || item.examName || 'Untitled Assessment',
      type: getAssessmentType(item.assessmentTypeName),
      date: item.completedDate || item.createdDate || new Date().toISOString().split('T')[0],
      score: item.total_score,
      classLevel: item.class_level_name,
      totalQuestions: item.total_questions || item.questionCount || 0,
      timeSpent: formatTimeSpent(item.time_used_seconds),
      status: getStatus(item),
      icon: getIconBySubject(item.subject || item.category),
      color: getColorBySubject(item.subject || item.category),
      rawData: item, // Keep original data for navigation
    }));
  };

  // Helper functions for data transformation
  const getAssessmentType = (type) => {
    if (!type) return 'Assessment';
    
    const typeLower = type.toLowerCase();
    if (typeLower.includes('exam')) return 'Exam';
    if (typeLower.includes('quiz')) return 'Quiz';
    if (typeLower.includes('test')) return 'Test';
    if (typeLower.includes('practice')) return 'Practice';
    return 'Assessment';
  };

  const formatTimeSpent = (seconds) => {
    if (!seconds || seconds === 0) return '0 secs';
  
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    
    const parts = [];
    
    if (hours > 0) {
      parts.push(`${hours} hr${hours > 1 ? 's' : ''}`);
    }
    
    if (minutes > 0) {
      parts.push(`${minutes} min${minutes > 1 ? 's' : ''}`);
    }
    
    if (remainingSeconds > 0 || parts.length === 0) {
      parts.push(`${remainingSeconds} sec${remainingSeconds !== 1 ? 's' : ''}`);
    }
    
    return parts.join(' ');
  };

  const getStatus = (item) => {
    if (item.status === 'completed' || item.isCompleted) {
      return 'completed';
    }
    if (item.status === 'in-progress' || item.isInProgress) {
      return 'in-progress';
    }
    return 'completed';
  };

  const getIconBySubject = (subject) => {
    const iconMap = {
      'mathematics': 'calculator',
      'math': 'calculator',
      'physics': 'bulb',
      'chemistry': 'fire',
      'biology': 'leaf',
      'english': 'book',
      'history': 'history',
      'geography': 'globe',
      'science': 'experiment',
      'computer': 'laptop',
      'default': 'file-text',
    };
    
    const subjectLower = subject?.toLowerCase() || '';
    for (const [key, icon] of Object.entries(iconMap)) {
      if (subjectLower.includes(key)) {
        return icon;
      }
    }
    return iconMap.default;
  };

  const getColorBySubject = (subject) => {
    const colorMap = {
      'mathematics': '#29d159',
      'math': '#29d159',
      'physics': '#4a90e2',
      'chemistry': '#ff6b6b',
      'biology': '#4CAF50',
      'english': '#9C27B0',
      'history': '#FF9800',
      'geography': '#795548',
      'science': '#2196F3',
      'computer': '#607D8B',
      'default': '#666666',
    };
    
    const subjectLower = subject?.toLowerCase() || '';
    for (const [key, color] of Object.entries(colorMap)) {
      if (subjectLower.includes(key)) {
        return color;
      }
    }
    return colorMap.default;
  };

  const calculateStats = (assessmentData) => {
    const completed = assessmentData.filter(item => item.status === 'completed').length;
    const completedWithScores = assessmentData.filter(
      item => item.status === 'completed' && item.score > 0
    );
    
    const averageScore = completedWithScores.length > 0
      ? Math.round(
          completedWithScores.reduce((acc, item) => acc + item.score, 0) / completedWithScores.length
        )
      : 0;
    
    setStats({ 
      completed, 
      averageScore,
      totalExams: assessmentData.length 
    });
  };

  const handleItemPress = (item) => {
    if (item.status === 'completed') {
      navigate(`/exam-result/${item.rawData.uid}`, { 
        state: { examData: item.rawData } 
      });
    } else if (item.status === 'in-progress') {
      console.log('Resume exam:', item.rawData.uid);
    }
    console.log('Pressed item:', item.rawData.uid);
  };


  const handleBackPress = () => {
    navigate(-1);
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchAssessmentResults();
  };

  const renderEmptyState = () => (
    <div className="empty-container">
      <HistoryOutlined className="empty-icon" />
      <h2 className="empty-title">No Exam History</h2>
      <p className="empty-text">
        You haven't taken any exams yet. Start your learning journey!
      </p>
      <button 
        className="start-button"
        onClick={() => navigate('/exams')}
      >
        Start Your First Exam
      </button>
    </div>
  );

  const renderErrorState = () => (
    <div className="error-container">
      <WarningOutlined className="error-icon" />
      <h2 className="error-title">Unable to Load History</h2>
      <p className="error-text">{error}</p>
      <button 
        className="retry-button"
        onClick={fetchAssessmentResults}
      >
        Retry
      </button>
    </div>
  );

  useEffect(() => {
    // Use passed data if available, otherwise fetch from API
    if (passedData && passedData.length > 0) {
      setData(passedData);
      calculateStats(passedData);
      setLoading(false);
    } else {
      fetchAssessmentResults();
    }
  }, []);

  // Show loading state
  if (loading && !refreshing) {
    return (
      <div className="container">
        <div className="header">
          <button onClick={handleBackPress} className="back-button">
            <ArrowLeftOutlined />
          </button>
          <h1 className="header-title">Exam History</h1>
          <div className="header-placeholder" />
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <p className="loading-text">Loading exam history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Header */}
      <div className="header">
        <button onClick={handleBackPress} className="back-button">
          <ArrowLeftOutlined />
        </button>
        <h1 className="header-title">Exam History</h1>
        <div className="header-placeholder" />
      </div>

      {/* Stats Summary */}
      <div className="stats-summary">
        <div className="stat-item">
          <div className="stat-number">{stats.totalExams}</div>
          <div className="stat-label">Total Exams</div>
        </div>
        <div className="stat-divider" />
        <div className="stat-item">
          <div className="stat-number">{stats.completed}</div>
          <div className="stat-label">Completed</div>
        </div>
        <div className="stat-divider" />
        <div className="stat-item">
          <div className="stat-number">{stats.averageScore}%</div>
          <div className="stat-label">Avg. Score</div>
        </div>
      </div>

      {/* Content */}
      <div className="content-area">
        {error && data.length === 0 ? (
          renderErrorState()
        ) : data.length === 0 ? (
          renderEmptyState()
        ) : (
          <>
            <div className="list-header">
              Showing {data.length} exam{data.length !== 1 ? 's' : ''}
            </div>
            <div className="list-container">
              {data.map((item, index) => (
                <HistoryItem
                  key={item.id}
                  item={item}
                  index={index}
                  onClick={handleItemPress}
                />
              ))}
            </div>
            {refreshing && (
              <div className="refresh-indicator">
                <div className="refresh-spinner"></div>
                Refreshing...
              </div>
            )}
            <button 
              className="refresh-button"
              onClick={onRefresh}
              disabled={refreshing}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ExamHistory;